// Copyright (c) Microsoft. All rights reserved.

import { ApplicationInsights, ICustomProperties } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { AccountInfo } from '@azure/msal-browser';

/**
 * Service for managing Application Insights telemetry on the frontend.
 * Tracks page views, custom events, exceptions, and user context.
 */
class AppInsightsService {
    private appInsights: ApplicationInsights | null = null;
    private reactPlugin: ReactPlugin | null = null;
    private isInitialized = false;

    /**
     * Initializes Application Insights with the provided connection string.
     * Should be called once during app startup.
     * @param connectionString - Application Insights connection string
     * @returns ReactPlugin instance for use with React application
     */
    public initialize(connectionString: string | undefined): ReactPlugin {
        if (this.isInitialized) {
            return this.reactPlugin as ReactPlugin;
        }

        if (!connectionString) {
            console.warn('Application Insights connection string not provided. Telemetry will not be collected.');
            this.reactPlugin = new ReactPlugin();
            return this.reactPlugin;
        }

        try {
            console.log('Initializing Application Insights with connection string:', 
                connectionString.substring(0, 50) + '...');

            this.reactPlugin = new ReactPlugin();
            this.appInsights = new ApplicationInsights({
                config: {
                    connectionString: connectionString,
                    enableAutoRouteTracking: true, // Automatically track route changes
                    disableFetchTracking: false, // Track fetch/XHR requests
                    enableCorsCorrelation: true, // Enable correlation with backend
                    enableRequestHeaderTracking: true,
                    enableResponseHeaderTracking: true,
                    correlationHeaderExcludedDomains: [], // Don't exclude any domains
                    disableAjaxTracking: false, // Track AJAX calls
                    maxBatchInterval: 5000, // Send telemetry every 5 seconds
                    disableTelemetry: false, // Ensure telemetry is enabled
                    extensions: [this.reactPlugin],
                    extensionConfig: {},
                },
            });

            this.appInsights.loadAppInsights();
            
            // Log detailed configuration for debugging
            const instrumentationKey = this.appInsights.config.instrumentationKey as string | undefined;
            console.log('[AppInsights] Configuration details:', {
                instrumentationKey: instrumentationKey ? instrumentationKey.substring(0, 10) + '...' : 'not set',
                endpointUrl: this.appInsights.config.endpointUrl,
                disableTelemetry: this.appInsights.config.disableTelemetry,
                maxBatchInterval: this.appInsights.config.maxBatchInterval,
                enableAutoRouteTracking: this.appInsights.config.enableAutoRouteTracking,
            });
            
            // Add telemetry initializer for debugging
            this.appInsights.addTelemetryInitializer((envelope) => {
                console.log('[AppInsights] Sending telemetry:', {
                    name: envelope.name,
                    time: envelope.time,
                    data: envelope.data,
                });
                return true; // Return true to send the telemetry
            });
            
            // Track a test page view to verify telemetry is working
            this.appInsights.trackPageView({ name: 'AppInitialization', uri: window.location.href });
            
            // Flush immediately to test connectivity
            void this.appInsights.flush();
            
            this.isInitialized = true;

            console.log('[AppInsights] Initialized successfully');
            console.log('[AppInsights] Check browser Network tab for POST requests to dc.services.visualstudio.com or dc.applicationinsights.azure.com');
            console.log('[AppInsights] If no requests appear, check: 1) Connection string is valid, 2) No Content Security Policy blocking, 3) No ad blocker');
        } catch (error) {
            console.error('Failed to initialize Application Insights:', error);
        }

        return this.reactPlugin as ReactPlugin;
    }

    /**
     * Sets the authenticated user context for all subsequent telemetry.
     * Should be called after user authenticates.
     * @param account - MSAL account information
     */
    public setAuthenticatedUserContext(account: AccountInfo | null): void {
        if (!this.appInsights || !account) {
            console.log('Cannot set user context - AppInsights not initialized or no account provided');
            return;
        }

        try {
            console.log('[AppInsights] Setting authenticated user context:', {
                userId: account.localAccountId,
                email: account.username,
                displayName: account.name,
            });

            // Set standard authenticated user ID
            this.appInsights.setAuthenticatedUserContext(
                account.localAccountId, // User ID
                account.tenantId, // Account ID (optional)
                true // Store in cookie for correlation across sessions
            );

            // Add additional user properties as custom properties
            const userProperties: ICustomProperties = {};

            if (account.username) {
                userProperties.userEmail = account.username;
            }

            if (account.name) {
                userProperties.userDisplayName = account.name;
            }

            // Set global properties that will be included with all telemetry
            this.appInsights.addTelemetryInitializer((envelope) => {
                if (envelope.data) {
                    envelope.data.userEmail = account.username;
                    envelope.data.userDisplayName = account.name;
                }
                return true;
            });

            // Send a test event to verify telemetry is working
            this.trackEvent('UserContextSet', {
                userId: account.localAccountId,
                userEmail: account.username,
                userDisplayName: account.name ?? 'not available',
            });

            console.log('[AppInsights] User context set. Test event "UserContextSet" sent.');
            
            // Flush to ensure event is sent immediately
            void this.appInsights.flush();
            console.log('[AppInsights] Flushed telemetry after user context set');
        } catch (error) {
            console.error('Failed to set user context:', error);
        }
    }

    /**
     * Clears the authenticated user context.
     * Should be called when user logs out.
     */
    public clearAuthenticatedUserContext(): void {
        if (!this.appInsights) {
            return;
        }

        try {
            this.appInsights.clearAuthenticatedUserContext();
            console.log('[AppInsights] User context cleared from telemetry');
        } catch (error) {
            console.error('Failed to clear user context:', error);
        }
    }

    /**
     * Tracks a custom event with optional properties and measurements.
     * @param name - Event name
     * @param properties - Custom properties (key-value pairs)
     * @param measurements - Custom measurements (numeric values)
     */
    public trackEvent(
        name: string,
        properties?: Record<string, string>,
        measurements?: Record<string, number>
    ): void {
        if (!this.appInsights) {
            return;
        }

        try {
            this.appInsights.trackEvent({ name, properties, measurements });
        } catch (error) {
            console.error('Failed to track event:', error);
        }
    }

    /**
     * Tracks an exception/error.
     * @param error - Error object or string
     * @param properties - Additional context properties
     */
    public trackException(error: Error | string, properties?: Record<string, string>): void {
        if (!this.appInsights) {
            return;
        }

        try {
            const exception = typeof error === 'string' ? new Error(error) : error;
            this.appInsights.trackException({ exception, properties });
        } catch (err) {
            console.error('Failed to track exception:', err);
        }
    }

    /**
     * Tracks a page view manually (useful for SPAs).
     * @param name - Page name
     * @param uri - Page URI
     */
    public trackPageView(name?: string, uri?: string): void {
        if (!this.appInsights) {
            return;
        }

        try {
            this.appInsights.trackPageView({ name, uri });
        } catch (error) {
            console.error('Failed to track page view:', error);
        }
    }

    /**
     * Tracks a metric/measurement.
     * @param name - Metric name
     * @param average - Average value
     * @param properties - Additional context properties
     */
    public trackMetric(name: string, average: number, properties?: Record<string, string>): void {
        if (!this.appInsights) {
            return;
        }

        try {
            this.appInsights.trackMetric({ name, average }, properties);
        } catch (error) {
            console.error('Failed to track metric:', error);
        }
    }

    /**
     * Gets the React plugin instance for wrapping the app.
     */
    public getReactPlugin(): ReactPlugin | null {
        return this.reactPlugin;
    }

    /**
     * Checks if Application Insights is initialized and ready.
     */
    public get initialized(): boolean {
        return this.isInitialized && this.appInsights !== null;
    }
}

// Export singleton instance
export const appInsightsService = new AppInsightsService();
